import { useState } from "react";
import PropTypes from "prop-types";
import { Button, Popconfirm } from "antd";
import { CheckCircleOutlined, StopOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import apiClient from "../../../helpers/apiClient";
import { useToast } from "../../../contexts/ToastContext";

/**
 * Takes a car out of service (or back in). A disabled car is hidden from the booking site and the
 * backend refuses any new booking or car swap onto it. Saves immediately after a confirm and reports
 * the new value through `onChange`, so the page can update its own copy of the car.
 */
function DisabledToggle({ car, onChange, size = "small" }) {
    const { t } = useTranslation("cars");
    const toast = useToast();
    const [saving, setSaving] = useState(false);
    const disabled = Boolean(car?.isDisabled);

    const toggle = async () => {
        if (!car?.id || saving) return;

        setSaving(true);
        try {
            const res = await apiClient.patch(`/api/car/${car.id}`, { isDisabled: !disabled });
            if (!res?.data?.success) throw new Error(res?.data?.message);
            onChange?.({ ...car, isDisabled: !disabled });
            toast.success({ message: !disabled ? t("carDisabledSuccess") : t("carEnabledSuccess") });
        } catch (error) {
            toast.error({ message: t("carDisableFailed"), description: error?.response?.data?.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Popconfirm
            title={disabled ? t("enableCarConfirmTitle") : t("disableCarConfirmTitle")}
            description={disabled ? t("enableCarConfirmDescription") : t("disableCarConfirmDescription")}
            onConfirm={toggle}
            okText={disabled ? t("enableCar") : t("disableCar")}
            okButtonProps={{ danger: !disabled }}
            cancelText={t("cancel")}
            disabled={saving}>
            <Button
                danger={!disabled}
                shape="round"
                size={size}
                loading={saving}
                aria-pressed={disabled}
                icon={disabled ? <CheckCircleOutlined /> : <StopOutlined />}>
                {disabled ? t("enableCar") : t("disableCar")}
            </Button>
        </Popconfirm>
    );
}

DisabledToggle.propTypes = {
    car: PropTypes.object,
    onChange: PropTypes.func,
    size: PropTypes.oneOf(["small", "middle", "large"]),
};

export default DisabledToggle;
